import React, { useState } from 'react';

/**
 * CodeExamples Component
 * Displays code examples in a right sidebar with language selection.
 * Theme-driven UI uses config CSS vars; code blocks stay dark (terminal style).
 */
const CodeExamples = ({ examples = [] }) => {
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('curl');

  if (!examples || examples.length === 0) {
    return null;
  }

  const copyToClipboard = async (code, index) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getCodeForLanguage = (example, language) => {
    if (!example.codes || !example.codes[language]) {
      return example.code || '';
    }
    return example.codes[language];
  };

  const containerStyle = {
    position: 'sticky',
    top: '76px',
    height: 'calc(100vh - 96px)',
    maxHeight: 'calc(100vh - 96px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingLeft: 'var(--spacing-md)',
    borderLeft: '1px solid var(--color-border-light)',
    width: '420px',
    minWidth: '420px',
    maxWidth: '420px',
    scrollBehavior: 'smooth'
  };

  const headerStyle = {
    marginBottom: 'var(--spacing-md)',
    paddingBottom: 'var(--spacing-md)',
    borderBottom: '1px solid var(--color-border-light)'
  };

  const languageTabsStyle = {
    display: 'flex',
    gap: 'var(--spacing-xs)',
    marginTop: 'var(--spacing-sm)',
    flexWrap: 'wrap'
  };

  const languageTabStyle = (isActive) => ({
    padding: '6px 12px',
    fontSize: 'var(--text-xs)',
    fontWeight: isActive ? '600' : '400',
    backgroundColor: isActive ? 'var(--color-primary)' : 'var(--color-bg-primary)',
    color: isActive ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
    border: `1px solid ${isActive ? 'var(--color-primary)' : 'var(--color-border)'}`,
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap'
  });

  const exampleStyle = {
    marginBottom: 'var(--spacing-lg)',
    padding: 'var(--spacing-md)',
    backgroundColor: 'var(--color-bg-secondary)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-sm)'
  };

  const titleStyle = {
    fontSize: 'var(--text-sm)',
    fontWeight: '600',
    marginBottom: 'var(--spacing-xs)',
    color: 'var(--color-text-primary)'
  };

  const descriptionStyle = {
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-sm)',
    fontSize: 'var(--text-xs)',
    lineHeight: '1.5'
  };

  const codeContainerStyle = {
    position: 'relative',
    backgroundColor: 'var(--color-bg-tertiary)',
    borderRadius: 'var(--radius-md)',
    padding: '0',
    marginTop: 'var(--spacing-sm)',
    overflow: 'hidden',
    border: '1px solid var(--color-border)'
  };

  const codeHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-xs) var(--spacing-sm)',
    backgroundColor: 'var(--color-bg-hover)',
    borderBottom: '1px solid var(--color-border)'
  };

  const languageLabelStyle = {
    fontSize: '11px',
    color: 'var(--color-text-tertiary)',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const copyButtonStyle = {
    padding: '4px 10px',
    fontSize: '11px',
    backgroundColor: 'var(--color-bg-hover)',
    border: '1px solid var(--color-border)',
    borderRadius: '4px',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontWeight: '500'
  };

  const codeContentStyle = {
    padding: 'var(--spacing-md)',
    overflowX: 'auto',
    backgroundColor: 'var(--color-bg-tertiary)'
  };

  const codeStyle = {
    fontFamily: 'var(--font-family-mono)',
    fontSize: '13px',
    lineHeight: '1.7',
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-bg-tertiary)',
    whiteSpace: 'pre',
    margin: 0,
    letterSpacing: '0.3px',
    '--code-color': 'var(--color-text-primary)'
  };

  return (
    <aside style={containerStyle}>
      <div style={headerStyle}>
        <h3 style={{ fontSize: 'var(--text-base)', marginBottom: 'var(--spacing-xs)', fontWeight: '600', color: 'var(--color-text-primary)' }}>
          Code Examples
        </h3>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)', marginBottom: 'var(--spacing-sm)' }}>
          Try these examples with your API
        </p>
        <div style={languageTabsStyle}>
          {['curl', 'python', 'javascript'].map(lang => (
            <button
              key={lang}
              style={languageTabStyle(selectedLanguage === lang)}
              onClick={() => setSelectedLanguage(lang)}
              onMouseEnter={(e) => {
                if (selectedLanguage !== lang) {
                  e.target.style.backgroundColor = 'var(--color-bg-hover)';
                  e.target.style.borderColor = 'var(--color-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedLanguage !== lang) {
                  e.target.style.backgroundColor = 'var(--color-bg-primary)';
                  e.target.style.borderColor = 'var(--color-border)';
                }
              }}
            >
              {lang === 'curl' ? 'cURL' : lang === 'javascript' ? 'JavaScript' : 'Python'}
            </button>
          ))}
        </div>
      </div>
      {examples.map((example, index) => {
        const code = getCodeForLanguage(example, selectedLanguage);
        if (!code) return null;
        
        return (
          <div key={index} style={exampleStyle}>
            <h4 style={titleStyle}>{example.title}</h4>
            {example.description && (
              <p style={descriptionStyle}>{example.description}</p>
            )}
            <div style={codeContainerStyle}>
              <div style={codeHeaderStyle}>
                <span style={languageLabelStyle}>{selectedLanguage === 'curl' ? 'Bash' : selectedLanguage === 'javascript' ? 'JavaScript' : 'Python'}</span>
                <button
                  style={copyButtonStyle}
                  onClick={() => copyToClipboard(code, index)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--color-primary)';
                    e.target.style.borderColor = 'var(--color-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'var(--color-bg-hover)';
                    e.target.style.borderColor = 'var(--color-border)';
                  }}
                >
                  {copiedIndex === index ? '✓ Copied' : 'Copy'}
                </button>
              </div>
              <div style={codeContentStyle}>
                <pre style={codeStyle}>
                  <code 
                    className="architecture-code-example"
                    style={{ 
                      color: 'var(--color-text-primary)',
                      backgroundColor: 'var(--color-bg-tertiary)'
                    }}
                  >
                    {code}
                  </code>
                </pre>
              </div>
            </div>
          </div>
        );
      })}
    </aside>
  );
};

export default CodeExamples;

